import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { Link, useParams } from "react-router-dom";
import Loading from "../../UI/Loading/Loading";
import AlertMessage from "../../UI/Alerts/AlertMessage";
import IOButton from "../../UI/Form/Button/IOButton";
import Flex from "../../UI/Flex/Flex";
import DateTime from "../../UI/DateTime";
import useConfirmDelete from "../../hooks/useConfirmDelete";
import { listOAuthClients, OAuthClient } from "../../api/oauthClients";
import {
  deleteOAuthToken,
  listOAuthTokens,
  OAuthToken,
} from "../../api/oauthTokens";
import { showToastWithTimeout } from "../../store/actions/message";

const PageContainer = styled.div`
  padding-right: 30px;
  @media (max-width: 800px) {
    padding: 0 30px;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 13px;
  margin: 10px 0 18px 0;

  a {
    color: ${(props) => props.theme.text.secondary};
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const Title = styled.div`
  font-size: larger;
  text-align: left;
  margin-bottom: 18px;
`;

const Card = styled.div`
  border: 1px solid ${(props) => props.theme.gray.gray300};
  border-radius: 6px;
  padding: 16px;
  max-width: 920px;
  background: ${(props) =>
    props.theme.type === "dark" ? props.theme.gray.gray900 : "white"};
`;

const Table = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 180px 180px 120px;
  gap: 10px 14px;
  align-items: center;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const HeaderCell = styled.div`
  font-weight: 700;
  opacity: 0.85;
`;

const Divider = styled.div`
  height: 1px;
  background: ${(props) => props.theme.gray.gray300};
  margin: 16px 0;
`;

type Params = { clientId: string };

export default function OAuthClientTokensPage() {
  const { clientId } = useParams<Params>();
  const clientIdNumber = Number(clientId);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [client, setClient] = useState<OAuthClient | null>(null);
  const [tokens, setTokens] = useState<OAuthToken[]>([]);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  const filteredTokens = useMemo(() => {
    return (tokens ?? []).filter((t) => t.client_id === clientId);
  }, [tokens, clientId]);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setError(null);
      setLoading(true);
      try {
        const [clients, nextTokens] = await Promise.all([
          listOAuthClients(signal),
          listOAuthTokens(signal),
        ]);
        const match = clients.find((c) => c.id === clientIdNumber) ?? null;
        setClient(match);
        setTokens(nextTokens);
      } catch (e: any) {
        if (e?.name === "CanceledError") {
          return;
        }
        setError(
          e?.response?.data?.message ??
            e?.message ??
            "Failed to load OAuth tokens."
        );
      } finally {
        setLoading(false);
      }
    },
    [clientIdNumber]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const onDelete = async (token: OAuthToken) => {
    setError(null);
    const id = token.id;
    setDeletingIds((prev) => new Set(prev).add(id));
    try {
      await deleteOAuthToken(id);
      await load();
      showToastWithTimeout("Token deleted.", "success");
    } catch (e: any) {
      const message =
        e?.response?.data?.message ?? e?.message ?? "Failed to delete token.";
      setError(message);
      showToastWithTimeout(message, "danger");
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const confirm = useConfirmDelete(
    onDelete,
    "Revoke token?",
    "This will revoke the token and cannot be undone."
  );

  return (
    <PageContainer data-cy="oauth-client-tokens-page">
      <Breadcrumbs data-cy="oauth-client-tokens-breadcrumbs">
        <Link to="/user-profile">User Profile</Link>
        <span>/</span>
        <Link to="/user-profile/oauth">OAuth Clients</Link>
        <span>/</span>
        <span>{client?.name ?? `Client ${clientId}`}</span>
      </Breadcrumbs>

      <Title>Tokens</Title>

      {error ? (
        <AlertMessage
          status="error"
          cy="oauth-client-tokens-error"
          stacked
          actions={null}
        >
          {error}
        </AlertMessage>
      ) : null}

      <Card>
        {loading ? (
          <div style={{ padding: 18 }}>
            <Loading />
          </div>
        ) : filteredTokens.length ? (
          <Table data-cy="oauth-token-list">
            <HeaderCell>Name</HeaderCell>
            <HeaderCell>Created</HeaderCell>
            <HeaderCell>Expiry</HeaderCell>
            <HeaderCell />

            {filteredTokens.map((t) => {
              const isDeleting = deletingIds.has(t.id);
              return (
                <React.Fragment key={t.id}>
                  <div data-cy="oauth-token-name-value">{t.name ?? ""}</div>
                  <div data-cy="oauth-token-created-at-value">
                    <DateTime date={t.created_at} />
                  </div>
                  <div data-cy="oauth-token-expires-at-value">
                    <DateTime date={t.expires_at} />
                  </div>
                  <Flex justifyContent="flex-end">
                    <IOButton
                      type="button"
                      outline
                      error
                      smallPadding
                      processing={isDeleting}
                      disabled={isDeleting}
                      onClick={() => confirm.setConfirming(t)}
                      cy="oauth-token-delete"
                    >
                      Revoke
                    </IOButton>
                  </Flex>
                  <Divider style={{ gridColumn: "1 / -1", margin: 6 }} />
                </React.Fragment>
              );
            })}
          </Table>
        ) : (
          <div data-cy="oauth-token-empty" style={{ opacity: 0.8 }}>
            No tokens found for this client.
          </div>
        )}
      </Card>

      {confirm.confirming}
    </PageContainer>
  );
}
